export type ProviderInstructions = {
    urlInstructions: string[],
    fileInstructions: string[],
    manualInstructions: string[],
    docUrl: string,
    docLabel: string,
    pdfGuide?: {url: string, label: string},
};

const okta: ProviderInstructions = {
    docUrl: 'https://help.okta.com/en-us/content/topics/apps/apps_app_integration_wizard_saml.htm',
    docLabel: 'View Okta documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Okta.pdf', label: 'Download Okta SSO Configuration Guide'},
    urlInstructions: [
        'Log in to your Okta admin console.',
        'Go to Applications and open your SAML application.',
        'Navigate to the Sign On tab.',
        'Locate the Identity Provider metadata link.',
        'Right-click the link or copy the URL.',
        'Paste the URL below.',
    ],
    fileInstructions: [
        'Log in to your Okta admin console.',
        'Go to Applications and open your SAML application.',
        'Navigate to the Sign On tab.',
        'Locate the Identity Provider metadata link.',
        'Click the link to open the XML in a new tab.',
        'Save the page as an XML file.',
        'Upload the file below.',
    ],
    manualInstructions: [
        'Log in to your Okta admin console.',
        'Go to Applications and open your SAML application.',
        'On the Sign On tab, click "View Setup Instructions".',
        'Copy the Identity Provider Single Sign-On URL.',
        'Copy the X.509 Certificate.',
        'Paste both values into the fields below.',
    ],
};

const microsoft: ProviderInstructions = {
    docUrl: 'https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/add-application-portal-setup-sso',
    docLabel: 'View Microsoft Entra ID documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Microsoft Entra.pdf', label: 'Download Microsoft Entra SSO Configuration Guide'},
    urlInstructions: [
        'Log in to the Azure portal.',
        'Go to Enterprise Applications and select your SAML app.',
        'Open the Single sign-on settings page.',
        'Under SAML Signing Certificate, find the App Federation Metadata Url.',
        'Copy the metadata URL.',
        'Paste the URL below.',
    ],
    fileInstructions: [
        'Log in to the Azure portal.',
        'Go to Enterprise Applications and select your SAML app.',
        'Open the Single sign-on settings page.',
        'Under SAML Signing Certificate, click Download next to "Federation Metadata XML".',
        'Save the downloaded XML file.',
        'Upload the file below.',
    ],
    manualInstructions: [
        'Log in to the Azure portal.',
        'Go to Enterprise Applications and select your SAML app.',
        'Open the Single sign-on settings page.',
        'Copy the Login URL from the setup section.',
        'Under SAML Signing Certificate, download the Certificate (Base64).',
        'Paste the Login URL and certificate contents into the fields below.',
    ],
};

const onelogin: ProviderInstructions = {
    docUrl: 'https://developers.onelogin.com/saml',
    docLabel: 'View OneLogin documentation',
    urlInstructions: [
        'Log in to your OneLogin admin portal.',
        'Open your SAML connector application.',
        'Go to the SSO tab.',
        'Locate the Issuer URL.',
        'Copy the Issuer URL.',
        'Paste the URL below.',
    ],
    fileInstructions: [
        'Log in to your OneLogin admin portal.',
        'Open your SAML connector application.',
        'Go to the SSO tab.',
        'Copy the Issuer URL and open it in a browser to download the metadata XML.',
        'Save the downloaded XML file.',
        'Upload the file below.',
    ],
    manualInstructions: [
        'Log in to your OneLogin admin portal.',
        'Open your SAML connector application.',
        'Go to the SSO tab.',
        'Copy the SAML 2.0 Endpoint (HTTP) — this is the Single Sign-On URL.',
        'Copy the X.509 Certificate.',
        'Paste both values into the fields below.',
    ],
};

const google: ProviderInstructions = {
    docUrl: 'https://support.google.com/a/answer/6087519',
    docLabel: 'View Google Workspace documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Google Workspaces.pdf', label: 'Download Google Workspaces SSO Configuration Guide'},
    urlInstructions: [
        'Log in to Google Workspace Admin Console.',
        'Go to Apps → Web and mobile apps.',
        'Select your custom SAML application.',
        'Google Workspace does not provide a metadata URL directly.',
        'Consider using the XML file download method instead.',
    ],
    fileInstructions: [
        'Log in to Google Workspace Admin Console.',
        'Go to Apps → Web and mobile apps.',
        'Create or open your custom SAML application.',
        'In the setup page, click "Download Metadata".',
        'Save the downloaded XML file.',
        'Upload the file below.',
    ],
    manualInstructions: [
        'Log in to Google Workspace Admin Console.',
        'Go to Apps → Web and mobile apps.',
        'Create or open your custom SAML application.',
        'Copy the SSO URL and Entity ID from the setup page.',
        'Copy the Certificate.',
        'Paste the SSO URL and certificate into the fields below.',
    ],
};

const pingfederate: ProviderInstructions = {
    docUrl: 'https://docs.pingidentity.com/pingfederate/13.0/pf_pf_landing_page.html',
    docLabel: 'View PingFederate documentation',
    urlInstructions: [
        'Log in to the PingFederate admin console.',
        'PingFederate does not typically provide a public metadata URL.',
        'Consider using the XML file export method instead.',
    ],
    fileInstructions: [
        'Log in to the PingFederate admin console.',
        'Go to System → Protocol Metadata → Metadata Export.',
        'On the Metadata Mode tab, leave the default selection.',
        'On the Connection Metadata tab, select your connection.',
        'Choose the signing certificate and click Export.',
        'Save the exported XML file.',
        'Upload the file below.',
    ],
    manualInstructions: [
        'Log in to the PingFederate admin console.',
        'Open your SP connection settings.',
        'Locate the IdP SSO Service URL in the connection configuration.',
        'Export the IdP signing certificate.',
        'Paste the SSO URL and certificate into the fields below.',
    ],
};

const other: ProviderInstructions = {
    docUrl: '',
    docLabel: '',
    urlInstructions: [
        'Log in to your identity provider\'s admin console.',
        'Locate the SAML application or SSO configuration section.',
        'Look for a metadata URL or metadata endpoint.',
        'Copy the metadata URL.',
        'Paste the URL below.',
    ],
    fileInstructions: [
        'Log in to your identity provider\'s admin console.',
        'Locate the SAML application or SSO configuration section.',
        'Look for an option to export or download the Federation Metadata XML.',
        'Download the XML file to your device.',
        'Upload the file below.',
    ],
    manualInstructions: [
        'Log in to your identity provider\'s admin console.',
        'Locate the SAML application or SSO configuration section.',
        'Find the Single Sign-On URL (also called Login URL or SSO Endpoint).',
        'Export or copy the signing certificate in PEM format.',
        'Paste both values into the fields below.',
    ],
};

export const PROVIDER_INSTRUCTIONS: Record<string, ProviderInstructions> = {
    okta,
    microsoft,
    onelogin,
    google,
    pingfederate,
    other,
};
